import { useCallback, useMemo, type ReactElement, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ChangePasswordInput, LoginInput, SessionUser } from '@kn/shared';
import { api, ApiError } from '../api/client';
import { AuthContext, type AuthContextValue } from './auth-context';

export const SESSION_QUERY_KEY = ['auth', 'me'] as const;

export function AuthProvider({ children }: { children: ReactNode }): ReactElement {
  const queryClient = useQueryClient();

  const sessionQuery = useQuery<SessionUser | null>({
    queryKey: SESSION_QUERY_KEY,
    queryFn: async () => {
      try {
        return await api.me();
      } catch (error) {
        // 401 = simplement pas de session : ce n'est pas une erreur applicative.
        if (error instanceof ApiError && (error.isUnauthenticated || error.status === 403)) {
          return null;
        }
        throw error;
      }
    },
    retry: false,
    staleTime: 30_000,
  });

  const login = useCallback(
    async (input: LoginInput) => {
      const user = await api.login(input);
      queryClient.setQueryData(SESSION_QUERY_KEY, user);
      return user;
    },
    [queryClient],
  );

  const logout = useCallback(async () => {
    await api.logout();
    queryClient.setQueryData(SESSION_QUERY_KEY, null);
    queryClient.removeQueries();
  }, [queryClient]);

  const changePassword = useCallback(
    async (input: ChangePasswordInput) => {
      const user = await api.changePassword(input);
      queryClient.setQueryData(SESSION_QUERY_KEY, user);
      return user;
    },
    [queryClient],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user: sessionQuery.data ?? null,
      isLoading: sessionQuery.isPending,
      login,
      logout,
      changePassword,
    }),
    [sessionQuery.data, sessionQuery.isPending, login, logout, changePassword],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
